// /opt/ptg01-v1/front-extranet/lib/AuthContext.tsx
// Context React qui expose le `user` une fois authentifié — alimenté par AuthGuard.
// Évite à chaque composant (Sidebar, TopBar, BottomTabBar…) de relire localStorage,
// fixe les bugs de désync cross-tab et fournit un point unique de vérité.
'use client';

import React, { createContext, useContext, useCallback, useMemo, useSyncExternalStore } from 'react';

export interface AuthUser {
    id?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    status?: string;
    emailVerified?: boolean;
    mustChangePassword?: boolean;
    [key: string]: unknown;
}

interface AuthContextValue {
    user: AuthUser | null;
    setUser: (user: AuthUser | null) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Le `user` est une donnée EXTERNE à React (localStorage, partagée entre
// onglets). On la lit avec useSyncExternalStore plutôt qu'en hydratant un état
// dans un effet : plus de rendu intermédiaire « déconnecté » au rafraîchissement,
// et la synchronisation cross-onglets tombe naturellement.

const CLE_UTILISATEUR = 'user';
/** Émis dans l'onglet courant — `storage` ne notifie que les AUTRES onglets. */
const EVENEMENT_UTILISATEUR = 'nx-auth-user';

/** Dernière valeur brute lue, et son parsé : garde l'instantané stable entre deux rendus. */
let cacheBrut: string | null = null;
let cacheParse: AuthUser | null = null;
/** Repli quand localStorage est indisponible (navigation privée, stockage bloqué). */
let valeurMemoire: AuthUser | null = null;
let stockageIndisponible = false;

function lireUtilisateur(): AuthUser | null {
    if (typeof window === 'undefined') return null;
    if (stockageIndisponible) return valeurMemoire;
    let brut: string | null;
    try {
        brut = localStorage.getItem(CLE_UTILISATEUR);
    } catch {
        stockageIndisponible = true;
        return valeurMemoire;
    }
    if (brut === cacheBrut) return cacheParse;
    cacheBrut = brut;
    try {
        cacheParse = brut ? (JSON.parse(brut) as AuthUser) : null;
    } catch {
        cacheParse = null;
    }
    return cacheParse;
}

/** Côté serveur, aucun utilisateur : évite une divergence d'hydratation. */
function lireUtilisateurServeur(): AuthUser | null {
    return null;
}

function souscrireUtilisateur(onChange: () => void): () => void {
    window.addEventListener('storage', onChange);
    window.addEventListener(EVENEMENT_UTILISATEUR, onChange);
    return () => {
        window.removeEventListener('storage', onChange);
        window.removeEventListener(EVENEMENT_UTILISATEUR, onChange);
    };
}

function ecrireUtilisateur(u: AuthUser | null): void {
    if (typeof window === 'undefined') return;
    valeurMemoire = u;
    try {
        if (u) localStorage.setItem(CLE_UTILISATEUR, JSON.stringify(u));
        else localStorage.removeItem(CLE_UTILISATEUR);
    } catch {
        stockageIndisponible = true;
    }
    window.dispatchEvent(new Event(EVENEMENT_UTILISATEUR));
}

/**
 * Provider à monter une fois dans le layout dashboard.
 * Hydrate l'état initial depuis localStorage pour éviter le flash UI au refresh.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
    const user = useSyncExternalStore(souscrireUtilisateur, lireUtilisateur, lireUtilisateurServeur);

    const setUser = useCallback((u: AuthUser | null) => {
        ecrireUtilisateur(u);
    }, []);

    const logout = useCallback(() => {
        setUser(null);
        if (typeof window !== 'undefined') {
            // Rechargement complet volontaire : vide au passage les caches React
            // Query et tout état client résiduel de la session précédente.
            window.location.assign(new URL('/auth/login', window.location.origin));
        }
    }, [setUser]);

    // Mémorisé : sans cela, chaque rendu du provider crée une nouvelle valeur et
    // rerend tous les consommateurs — et rend inexploitable toute dépendance à
    // `auth` dans un effet (l'objet change en permanence).
    const valeur = useMemo(() => ({ user, setUser, logout }), [user, setUser, logout]);

    return (
        <AuthContext.Provider value={valeur}>
            {children}
        </AuthContext.Provider>
    );
}

/**
 * Hook standard pour consommer le user authentifié.
 * Usage : `const { user, logout } = useAuth();`
 */
export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used inside <AuthProvider>');
    }
    return ctx;
}

/**
 * Variante non-throw — utile pour les composants qui peuvent vivre hors du dashboard
 * (ex: layouts auth/login). Retourne null si pas de provider.
 */
export function useAuthOptional(): AuthContextValue | null {
    return useContext(AuthContext);
}
